/**
 * Recurrence utilities for generating event series dates
 * Uses RRULE library for robust date generation with edge case handling
 */

import { RRule } from 'rrule';
import type { RecurrenceType } from '../types/events.types';

const FREQ_MAP: Record<RecurrenceType, number> = {
  none: RRule.WEEKLY, // Not used, but needed for type completeness
  weekly: RRule.WEEKLY,
  biweekly: RRule.WEEKLY,
  monthly: RRule.MONTHLY,
};

const INTERVAL_MAP: Record<RecurrenceType, number> = {
  none: 1,
  weekly: 1,
  biweekly: 2,
  monthly: 1,
};

/**
 * Generate recurrence dates using RRULE
 * Handles edge cases like DST transitions and month-end dates
 *
 * @param startDate - The start date/time for the first occurrence
 * @param recurrenceType - Type of recurrence (weekly, biweekly, monthly)
 * @param count - Number of occurrences to generate
 * @returns Array of Date objects for each occurrence
 */
export function generateRecurrenceDates(
  startDate: Date,
  recurrenceType: RecurrenceType,
  count: number
): Date[] {
  if (recurrenceType === 'none' || count < 2) {
    return [startDate];
  }

  const rule = new RRule({
    freq: FREQ_MAP[recurrenceType],
    interval: INTERVAL_MAP[recurrenceType],
    count,
    dtstart: startDate,
  });

  return rule.all();
}

/**
 * Get human-readable description of recurrence
 *
 * @param recurrenceType - Type of recurrence
 * @param count - Number of occurrences
 * @returns Human-readable string like "Every week for 4 occurrences"
 */
export function getRecurrenceDescription(
  recurrenceType: RecurrenceType,
  count: number
): string {
  if (recurrenceType === 'none') {
    return 'Does not repeat';
  }

  const labels: Record<RecurrenceType, string> = {
    none: 'week',
    weekly: 'week',
    biweekly: '2 weeks',
    monthly: 'month',
  };

  return `Every ${labels[recurrenceType]} for ${count} occurrences`;
}

/**
 * Generate RRULE string for storage in database
 *
 * @param recurrenceType - Type of recurrence
 * @param count - Number of occurrences
 * @returns RRULE string like "FREQ=WEEKLY;INTERVAL=1;COUNT=4"
 */
export function generateRRuleString(
  recurrenceType: RecurrenceType,
  count: number
): string {
  if (recurrenceType === 'none') {
    return '';
  }

  const freqMap: Record<RecurrenceType, string> = {
    none: 'WEEKLY',
    weekly: 'WEEKLY',
    biweekly: 'WEEKLY',
    monthly: 'MONTHLY',
  };

  const interval = INTERVAL_MAP[recurrenceType];
  return `FREQ=${freqMap[recurrenceType]};INTERVAL=${interval};COUNT=${count}`;
}

/**
 * Format dates for preview display
 *
 * @param dates - Array of dates to format
 * @returns Array of formatted date strings
 */
export function formatDatesForPreview(dates: Date[]): string[] {
  return dates.map((date) => {
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${weekday}, ${month} ${day}, ${year}`;
  });
}


